/**
 * OTLP live-telemetry plugin — Studio's first workbench plugin.
 *
 * Owns the live feed (one SSE connection, mounted OUTSIDE the board by the
 * plugin host) and contributes UI at two seams:
 *   - the board overlay: the request-flow particle layer (rate → dots);
 *   - the details panel: per-edge + per-node RED telemetry sections.
 *
 * The board holds no telemetry code — it renders the registered layer and the
 * DetailsPane renders the registered sections. Composability: the sections show
 * whatever metrics the feed carries (rate always; error rate + latency when the
 * source sampled raw traces), and self-hide when nothing is flowing.
 */
import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ParticleFlowLayer,
  LiveToggle,
  TelemetryEdgeLayer,
  ConformanceEdgeLayer,
  DriftTopologyPublisher,
  LiveTrafficProvider,
  createLiveTrafficStore,
  LiveStatsProvider,
  createLiveStatsStore,
  LiveHistoryProvider,
  createLiveHistoryStore,
  LiveFlowsProvider,
  createLiveFlowsStore,
  TraceLinkProvider,
  buildTraceLinks,
  EdgeTelemetrySection,
  ModuleTelemetrySection,
  WORKBENCH_PLUGIN_API_VERSION,
  toggleFlowVisible,
  type TraceBackendKind,
  type WorkbenchPlugin,
} from "@archlang/app-shell/board";
import { useLiveTraffic } from "../../useLiveTraffic.js";
import { apiFetch } from "../../../api/client.js";
import { TraceSourcePage } from "./TraceSourcePage.js";

export interface OtlpPluginConfig {
  readonly slug: string;
  readonly repo: string;
}

/** Provider bound to a repo: owns the live-traffic + live-stats stores and feeds
 *  both from the repo's live-traces SSE (one connection). Mounted OUTSIDE the
 *  board by the plugin host; the contributed layer + sections read them through
 *  their respective providers. */
function makeTelemetryProvider(cfg: OtlpPluginConfig) {
  return function OtlpTelemetryProvider({ children }: { children: ReactNode }) {
    const traffic = useMemo(() => createLiveTrafficStore(), []);
    const stats = useMemo(() => createLiveStatsStore(), []);
    const history = useMemo(() => createLiveHistoryStore(), []);
    const flows = useMemo(() => createLiveFlowsStore(), []);
    useLiveTraffic(cfg.slug, cfg.repo, traffic, stats, history, flows);
    // Deep-links to the backend trace UI — built from the same trace-source
    // config the overlay uses (shared react-query key with RepoSettings).
    const srcQuery = useQuery({
      queryKey: ["repo-trace-source", cfg.slug, cfg.repo],
      queryFn: () =>
        apiFetch<{ traceSource: { kind: TraceBackendKind; endpoint: string } | null }>(
          `/api/ws/${encodeURIComponent(cfg.slug)}/r/${encodeURIComponent(cfg.repo)}/trace-source`,
        ),
    });
    const links = useMemo(() => {
      const c = srcQuery.data?.traceSource;
      return c ? buildTraceLinks(c.kind, c.endpoint) : null;
    }, [srcQuery.data]);
    return (
      <LiveTrafficProvider value={traffic}>
        <LiveStatsProvider value={stats}>
          <LiveHistoryProvider value={history}>
            <LiveFlowsProvider value={flows}>
              <TraceLinkProvider value={links}>{children}</TraceLinkProvider>
            </LiveFlowsProvider>
          </LiveHistoryProvider>
        </LiveStatsProvider>
      </LiveTrafficProvider>
    );
  };
}

/** Build the OTLP plugin for a repo. Memoize the call on [slug, repo] at the
 *  mount site so the provider isn't rebuilt (remounted) every render. */
export function otlpPlugin(cfg: OtlpPluginConfig): WorkbenchPlugin {
  return {
    id: "otlp",
    title: "Live telemetry",
    apiVersion: WORKBENCH_PLUGIN_API_VERSION,
    providers: [makeTelemetryProvider(cfg)],
    contributes: {
      boardLayers: [
        { id: "edge-health", Layer: TelemetryEdgeLayer },
        { id: "conformance", Layer: ConformanceEdgeLayer },
        { id: "flow", Layer: ParticleFlowLayer },
        // Headless: derives cross-space drift topology (live stats + model) and
        // publishes it to the board solver, which folds it in as violation edges.
        { id: "drift-topology", Layer: DriftTopologyPublisher },
      ],
      detailSections: [
        {
          id: "edge-telemetry",
          appliesTo: (s) => s.kind === "edge",
          Section: EdgeTelemetrySection,
        },
        {
          id: "module-telemetry",
          appliesTo: (s) => s.kind === "module",
          Section: ModuleTelemetrySection,
        },
      ],
      // The "Live" board-header toggle: turns the request-flow animation on/off
      // (off by default). Present only where telemetry is wired (this plugin).
      headerItems: [{ id: "live-toggle", node: <LiveToggle /> }],
      // The keyboard/palette peer of the Live toggle — one immediate command in
      // the root ⌘K palette that flips the same flow-visibility store.
      commands: [
        {
          id: "otlp.toggle-live",
          title: "Toggle Live",
          keywords: ["telemetry", "flow", "animation", "traffic"],
          group: "Telemetry",
          run: () => toggleFlowVisible(),
        },
      ],
      // App-menu peer of the toggle — a brand-menu item under a Telemetry
      // section. Gated to repo context (no live traffic without a repo).
      menuItems: [
        {
          id: "otlp.menu.toggle-live",
          group: "Telemetry",
          label: "Toggle live traffic",
          when: (ctx) => ctx.activeRepo != null,
          run: () => toggleFlowVisible(),
        },
      ],
      // Right-click a board node → toggle the live-traffic flow, gated to repo
      // context. Rendered after the built-in node menu rows.
      contextMenu: [
        {
          id: "otlp.ctx.node-live",
          target: "node",
          when: (ctx) => ctx.activeRepo != null,
          items: () => [{ label: "Toggle live traffic", run: () => toggleFlowVisible() }],
        },
      ],
      // The trace-source config, contributed as a repo-scope settings page
      // (retires the hardcoded `repo-traffic` tab).
      settingsPages: [
        {
          id: "otlp.settings.trace-source",
          scope: "repo",
          title: "Live traffic",
          when: (ctx) => ctx.activeRepo != null,
          Page: TraceSourcePage,
        },
      ],
    },
  };
}
